import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    Title,
    Filler,
} from 'chart.js';
import { Pie, Bar, Line, Doughnut } from 'react-chartjs-2';
import {
    FaMoneyBillWave, FaRegHourglass, FaChartLine,
    FaCalendarAlt, FaTimesCircle, FaExchangeAlt, FaChartBar,
    FaFutbol, FaLayerGroup, FaUsers, FaArrowDown, FaBalanceScale, FaCogs
} from 'react-icons/fa';
import './FinancialAnalytics.css';
import API_BASE_URL from '../../config/api';

ChartJS.register(
    ArcElement, Tooltip, Legend,
    CategoryScale, LinearScale, BarElement,
    PointElement, LineElement, Title, Filler
);

const MONTH_NAMES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
    'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const STATUS_COLORS = {
    Completed: '#43e97b',
    Pending: '#feb47b',
    Failed: '#ff7e5f',
};
const STATUS_LABELS = {
    Completed: 'Concluído',
    Pending: 'Pendente',
    Failed: 'Falhado',
};
const SPORT_PALETTE = [
    'rgba(37,117,252,0.82)',
    'rgba(255,126,95,0.82)',
    'rgba(17,153,142,0.82)',
    'rgba(106,17,203,0.82)',
    'rgba(247,151,30,0.82)',
    'rgba(0,198,255,0.82)',
    'rgba(67,233,123,0.82)',
    'rgba(248,87,166,0.82)',
    'rgba(255,210,0,0.82)',
    'rgba(0,114,255,0.82)',
    'rgba(255,74,74,0.82)',
    'rgba(0,212,170,0.82)',
];

// Cost category colours for stacked bar
const COST_COLORS = {
    treinadores: 'rgba(37,117,252,0.85)',
    atletas: 'rgba(67,233,123,0.85)',
    coordenacao: 'rgba(0,198,255,0.85)',
    operacional: 'rgba(247,151,30,0.85)',
    arbitragem: 'rgba(255,126,95,0.85)',
    inscricoes: 'rgba(16,185,129,0.85)',
    equipamentos: 'rgba(14,165,233,0.85)',
    custom: 'rgba(148,163,184,0.85)',
};
const BAR_HEIGHT = 44;
const BAR_MIN = 180;

const METHOD_COLORS = [
    'rgba(30,60,114,0.85)',
    'rgba(37,117,252,0.85)',
    'rgba(106,17,203,0.85)',
    'rgba(0,198,255,0.85)',
];

// ── helpers ──────────────────────────────────────────────────────────────────
const parseMYM = (str) => {
    const [y, m] = str.split('-');
    return { year: y, month: m };
};
const monthLabel = (str, showYear = true) => {
    const { year, month } = parseMYM(str);
    return showYear
        ? `${MONTH_NAMES[parseInt(month, 10) - 1]} ${year}`
        : MONTH_NAMES[parseInt(month, 10) - 1];
};

const wrapText = (text, maxLength) => {
    if (!text) return [];
    const words = text.split(' ');
    const lines = [];
    let currentLine = '';

    words.forEach(word => {
        if ((currentLine + word).length > maxLength) {
            lines.push(currentLine.trim());
            currentLine = word + ' ';
        } else {
            currentLine += word + ' ';
        }
    });
    lines.push(currentLine.trim());
    return lines;
};

// ── common chart option pieces ────────────────────────────────────────────────
const TOOLTIP_BASE = {
    backgroundColor: 'rgba(255,255,255,0.97)',
    titleColor: '#1a202c',
    bodyColor: '#4a5568',
    borderColor: 'rgba(0,0,0,0.08)',
    borderWidth: 1,
    padding: 12,
    boxPadding: 8,
    usePointStyle: true,
};
const AXIS_BASE = {
    grid: { color: 'rgba(0,0,0,0.04)', drawBorder: false },
    ticks: { font: { size: 11, family: "'Inter', sans-serif" }, color: '#718096' },
};
const LEGEND_BASE = {
    position: 'bottom',
    labels: { padding: 16, usePointStyle: true, font: { size: 12, family: "'Inter', sans-serif" } },
};

// ── chart option builders (module-level — no state deps) ──────────────────────
const pieOpts = (fmtCb) => ({
    responsive: true, maintainAspectRatio: false,
    plugins: {
        legend: LEGEND_BASE,
        tooltip: { ...TOOLTIP_BASE, displayColors: true, callbacks: { label: fmtCb } },
    },
    animation: { duration: 1500, easing: 'easeOutQuart' },
});

const barOpts = (stacked = false, tickFmt = v => '€' + v.toLocaleString('pt-PT'), ttFmt = ctx => ` €${ctx.raw.toFixed(2)}`) => ({
    responsive: true, maintainAspectRatio: false,
    plugins: {
        legend: stacked ? LEGEND_BASE : { display: false },
        tooltip: { ...TOOLTIP_BASE, callbacks: { label: ttFmt } },
    },
    scales: {
        y: {
            ...AXIS_BASE, beginAtZero: true, stacked,
            ticks: { ...AXIS_BASE.ticks, callback: tickFmt },
        },
        x: { ...AXIS_BASE, grid: { display: false }, stacked },
    },
    animation: { duration: 900, easing: 'easeOutQuart' },
});

const lineOpts = (ttFmt) => ({
    responsive: true, maintainAspectRatio: false,
    plugins: {
        legend: { display: false },
        tooltip: { ...TOOLTIP_BASE, callbacks: { label: ttFmt } },
    },
    scales: {
        y: {
            ...AXIS_BASE, beginAtZero: true,
            ticks: { ...AXIS_BASE.ticks, callback: v => '€' + v.toLocaleString('pt-PT') },
        },
        x: { ...AXIS_BASE, grid: { display: false } },
    },
    animation: { duration: 900, easing: 'easeOutQuart' },
});

const hBarOpts = (rows = []) => ({
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: { display: false },
        tooltip: {
            ...TOOLTIP_BASE,
            callbacks: {
                label: ctx => ` €${ctx.raw.toFixed(2)}`,
                afterLabel: ctx => {
                    const row = rows[ctx.dataIndex];
                    if (!row) return '';

                    const lines = [];

                    if (row.l1Cost !== undefined && row.l1Cost > 0) {
                        lines.push(` Nível 1: €${row.l1Cost.toFixed(2)}`);
                        if (row.l1Names) wrapText(row.l1Names, 50).forEach(l => lines.push(`   ${l}`));
                    }
                    if (row.l2Cost !== undefined && row.l2Cost > 0) {
                        lines.push(` Nível 2: €${row.l2Cost.toFixed(2)}`);
                        if (row.l2Names) wrapText(row.l2Names, 50).forEach(l => lines.push(`   ${l}`));
                    }
                    if (row.l3Cost !== undefined && row.l3Cost > 0) {
                        lines.push(` Nível 3: €${row.l3Cost.toFixed(2)}`);
                        if (row.l3Names) wrapText(row.l3Names, 50).forEach(l => lines.push(`   ${l}`));
                    }
                    if (row.athleteCost !== undefined && row.athleteCost > 0) lines.push(` Atletas: €${row.athleteCost.toFixed(2)}`);
                    if (row.coordinationCost !== undefined) lines.push(` Coordenação: €${row.coordinationCost.toFixed(2)}`);
                    if (row.count !== undefined) lines.push(` ${row.count} pagamento${row.count !== 1 ? 's' : ''}`);

                    return lines.length > 0 ? lines : '';
                },
            },
        },
    },
    scales: {
        x: {
            ...AXIS_BASE,
            ticks: {
                ...AXIS_BASE.ticks,
                callback: v => '€' + v.toLocaleString('pt-PT'),
            },
            grid: { color: 'rgba(0,0,0,0.04)' },
        },
        y: {
            ...AXIS_BASE,
            grid: { display: false },
            ticks: {
                ...AXIS_BASE.ticks,
                font: { size: 12, family: "'Inter', sans-serif", weight: '500' },
            },
        },
    },
    animation: { duration: 1200, easing: 'easeOutQuart' },
});

// ── FinancialAnalytics ────────────────────────────────────────────────────────
const FinancialSkeleton = () => (
    <div className="financial-analytics-container skeleton-loading">
        <div className="analytics-overview-grid">
            {[1, 2, 3, 4].map(i => (
                <div key={i} className="stat-card-skeleton">
                    <div className="skeleton skeleton-icon" />
                    <div className="stat-info-skeleton">
                        <div className="skeleton skeleton-label" />
                        <div className="skeleton skeleton-stat" />
                    </div>
                </div>
            ))}
        </div>

        <div className="charts-grid">
            {[1, 2].map(i => (
                <div key={i} className="chart-item-skeleton">
                    <div className="skeleton skeleton-title" />
                    <div className="skeleton skeleton-circle" />
                </div>
            ))}
        </div>

        <div className="skeleton skeleton-filter-bar" />

        <div className="chart-item-skeleton full-width">
            <div className="skeleton skeleton-title" />
            <div className="skeleton skeleton-rect" />
        </div>
    </div>
);

const FinancialAnalytics = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Filters
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
    const [selectedMonth, setSelectedMonth] = useState('all');
    const [teamSportFilter, setTeamSportFilter] = useState('Todas');
    const [escalaoSportFilter, setEscalaoSportFilter] = useState('Todas');
    const [chartView, setChartView] = useState('bar'); // 'bar' | 'line'

    useEffect(() => {
        fetchFinancialData(selectedYear, selectedMonth);
    }, [selectedYear, selectedMonth]);

    const fetchFinancialData = async (year, month) => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const params = {};
            if (year && year !== 'all') params.year = year;
            if (month && month !== 'all') params.month = month;

            const res = await axios.get(`${API_BASE_URL}/api/ClubAnalytics/financial`, {
                headers: { Authorization: `Bearer ${token}` },
                params
            });
            setData(res.data);
        } catch (e) {
            console.error(e);
            setError('Erro ao carregar estatísticas financeiras.');
        } finally {
            setLoading(false);
        }
    };

    // ── derived lists ─────────────────────────────────────────────────────────
    const availableYears = useMemo(() => {
        if (!data) return [];
        return [...new Set(data.paymentStatsByMonth
            .map(m => parseMYM(m.month).year)
            .filter(y => y !== undefined && parseInt(y, 10) > 1900)
        )].sort((a, b) => b - a);
    }, [data]);

    const availableMonths = useMemo(() => {
        if (!data || selectedYear === 'all') return [];
        return data.paymentStatsByMonth
            .filter(m => parseMYM(m.month).year === selectedYear)
            .map(m => parseMYM(m.month).month);
    }, [data, selectedYear]);

    // Reset month when year changes
    const handleYearChange = (y) => {
        setSelectedYear(y);
        setSelectedMonth('all');
    };

    // Filtered paymentStatsByMonth rows
    const filteredStats = useMemo(() => {
        if (!data) return [];
        return data.paymentStatsByMonth.filter(m => {
            const { year, month } = parseMYM(m.month);
            if (selectedYear !== 'all' && year !== selectedYear) return false;
            if (selectedMonth !== 'all' && month !== selectedMonth) return false;
            return true;
        });
    }, [data, selectedYear, selectedMonth]);

    // Summary KPIs over filtered window
    const kpi = useMemo(() => {
        const revenue = filteredStats.reduce((s, m) => s + m.completedAmount, 0);
        const pending = filteredStats.reduce((s, m) => s + m.pendingAmount, 0);
        const failed = filteredStats.reduce((s, m) => s + m.failedAmount, 0);
        const count = filteredStats.reduce((s, m) => s + m.totalCount, 0);
        const months = filteredStats.length;
        const avg = months > 0 ? revenue / months : 0;
        const best = filteredStats.length > 0
            ? filteredStats.reduce((a, b) => b.completedAmount > a.completedAmount ? b : a)
            : null;
        return { revenue, pending, failed, count, avg, best };
    }, [filteredStats]);

    // Cumulative revenue (running total) for filtered window
    const cumulativeAmounts = useMemo(() => {
        let acc = 0;
        return filteredStats.map(m => { acc += m.completedAmount; return acc; });
    }, [filteredStats]);

    // ───── Segregation for unassigned data ─────
    const sportRowsRaw = useMemo(() => data?.revenueBySport ?? [], [data]);
    const sportRows = useMemo(() => sportRowsRaw.filter(s => s.sport !== 'Sem Modalidade'), [sportRowsRaw]);
    const unassignedSport = useMemo(() => sportRowsRaw.find(s => s.sport === 'Sem Modalidade'), [sportRowsRaw]);

    const teamRowsRaw = useMemo(() => data?.revenueByTeam ?? [], [data]);

    // Unique sports for the filters
    const uniqueSports = useMemo(() => {
        const fromTeams = teamRowsRaw.map(r => r.team.split(' - ')[0]);
        const fromCosts = (data?.costsBySport ?? []).map(s => s.sport);
        const sports = [...new Set([...fromTeams, ...fromCosts])].filter(Boolean);
        return ['Todas', ...sports.sort()];
    }, [teamRowsRaw, data?.costsBySport]);

    const teamRows = useMemo(() => {
        let rows = teamRowsRaw.filter(r => r.team !== 'Sem Equipa');
        if (teamSportFilter !== 'Todas') {
            rows = rows.filter(r => r.team.startsWith(`${teamSportFilter} - `));
        }
        return rows;
    }, [teamRowsRaw, teamSportFilter]);

    const unassignedTeam = useMemo(() => teamRowsRaw.find(r => r.team === 'Sem Equipa'), [teamRowsRaw]);

    // Derived escalão rows for the escalão costs chart
    const escalaoRows = useMemo(() => {
        if (!data?.costsByEscalao) return [];
        let rows = data.costsByEscalao;
        if (escalaoSportFilter !== 'Todas') {
            rows = rows.filter(e => e.sportName === escalaoSportFilter);
        }
        return rows;
    }, [data?.costsByEscalao, escalaoSportFilter]);

    // ── memoized chart data ────────────────────────────────────────────────────
    const labels = useMemo(
        () => filteredStats.map(m => monthLabel(m.month, selectedYear === 'all')),
        [filteredStats, selectedYear]
    );

    // 1. Pie – payment status (count)
    const statusPieData = useMemo(() => ({
        labels: data?.paymentsByStatus.map(s => STATUS_LABELS[s.status] || s.status) ?? [],
        datasets: [{
            data: data?.paymentsByStatus.map(s => s.count) ?? [],
            backgroundColor: data?.paymentsByStatus.map(s => STATUS_COLORS[s.status] || '#1e3c72') ?? [],
            borderWidth: 0, hoverOffset: 15,
        }],
    }), [data]);

    // 2. Doughnut – revenue by method
    const methodDonutData = useMemo(() => ({
        labels: data?.revenueByMethod.map(m => m.method) ?? [],
        datasets: [{
            data: data?.revenueByMethod.map(m => m.totalAmount) ?? [],
            backgroundColor: METHOD_COLORS.slice(0, data?.revenueByMethod.length ?? 0),
            borderWidth: 0, hoverOffset: 15,
        }],
    }), [data]);

    // 3. Bar/Line – monthly revenue (filtered)
    const monthlyRevenueData = useMemo(() => ({
        labels,
        datasets: [{
            label: 'Receita (€)',
            data: filteredStats.map(m => m.completedAmount),
            backgroundColor: 'rgba(67,233,123,0.75)',
            borderColor: 'rgba(67,233,123,1)',
            borderRadius: 8,
            borderSkipped: false,
            fill: true,
            tension: 0.4,
            pointBackgroundColor: 'rgba(67,233,123,1)',
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            pointRadius: 5,
            pointHoverRadius: 7,
        }],
    }), [labels, filteredStats]);

    // 4. Stacked bar – completed / pending / failed amounts per month
    const stackedStatusData = useMemo(() => ({
        labels,
        datasets: [
            { label: 'Concluído', data: filteredStats.map(m => m.completedAmount), backgroundColor: 'rgba(67,233,123,0.8)', borderRadius: 6, borderSkipped: false, stack: 'status' },
            { label: 'Pendente', data: filteredStats.map(m => m.pendingAmount), backgroundColor: 'rgba(254,180,123,0.85)', borderRadius: 6, borderSkipped: false, stack: 'status' },
            { label: 'Falhado', data: filteredStats.map(m => m.failedAmount), backgroundColor: 'rgba(255,126,95,0.85)', borderRadius: 6, borderSkipped: false, stack: 'status' },
        ],
    }), [labels, filteredStats]);

    // 5. Bar – payment count per month
    const countBarData = useMemo(() => ({
        labels,
        datasets: [
            { label: 'Concluídos', data: filteredStats.map(m => m.completedCount), backgroundColor: 'rgba(37,117,252,0.8)', borderRadius: 6, borderSkipped: false, stack: 'count' },
            { label: 'Pendentes', data: filteredStats.map(m => m.pendingCount), backgroundColor: 'rgba(254,180,123,0.85)', borderRadius: 6, borderSkipped: false, stack: 'count' },
            { label: 'Falhados', data: filteredStats.map(m => m.failedCount), backgroundColor: 'rgba(255,126,95,0.85)', borderRadius: 6, borderSkipped: false, stack: 'count' },
        ],
    }), [labels, filteredStats]);

    // 6. Line – cumulative revenue
    const cumulativeData = useMemo(() => ({
        labels,
        datasets: [{
            label: 'Receita Acumulada (€)',
            data: cumulativeAmounts,
            borderColor: 'rgba(106,17,203,1)',
            backgroundColor: 'rgba(106,17,203,0.08)',
            fill: true,
            tension: 0.4,
            pointBackgroundColor: 'rgba(106,17,203,1)',
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            pointRadius: 5,
            pointHoverRadius: 7,
        }],
    }), [labels, cumulativeAmounts]);

    // 7. Horizontal Bar – receita por modalidade
    const sportBarData = useMemo(() => ({
        labels: sportRows.map(s => s.sport),
        datasets: [{
            label: 'Receita (€)',
            data: sportRows.map(s => s.totalAmount),
            backgroundColor: sportRows.map((_, i) => SPORT_PALETTE[i % SPORT_PALETTE.length]),
            borderRadius: 6,
            borderSkipped: false,
        }],
    }), [sportRows]);

    // 8. Horizontal Bar – receita por equipa
    const teamBarData = useMemo(() => ({
        labels: teamRows.map(r => r.team),
        datasets: [{
            label: 'Receita (€)',
            data: teamRows.map(r => r.totalAmount),
            backgroundColor: teamRows.map((_, i) => SPORT_PALETTE[i % SPORT_PALETTE.length]),
            borderRadius: 6,
            borderSkipped: false,
        }],
    }), [teamRows]);

    // 9. Receita dos Sócios (Unassigned)
    const membersRevenueData = useMemo(() => ({
        labels: ['Receita dos Sócios'],
        datasets: [{
            label: 'Receita (€)',
            data: [unassignedSport?.totalAmount ?? 0],
            backgroundColor: ['rgba(37, 117, 252, 0.8)'],
            borderRadius: 6,
            borderSkipped: false,
        }],
    }), [unassignedSport]);

    // ── derived heights & labels ───────────────────────────────────────────────
    const teamChartHeight = Math.max(BAR_MIN, teamRows.length * BAR_HEIGHT);
    const sportChartHeight = Math.max(BAR_MIN, sportRows.length * BAR_HEIGHT);
    const bestLabel = kpi.best ? monthLabel(kpi.best.month) : '—';

    if (loading) return <FinancialSkeleton />;
    if (error) return <div className="analytics-error">{error}</div>;
    if (!data) return null;

    return (
        <div className="financial-analytics-container">

            {/* ── Period filter bar ── */}
            <div className="period-filter-bar">
                <div className="period-filter-group">
                    <FaCalendarAlt className="filter-icon" />
                    <label>Ano</label>
                    <select value={selectedYear} onChange={e => handleYearChange(e.target.value)} className="period-select">
                        <option value="all">Todos</option>
                        {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                </div>
                <div className="period-filter-group">
                    <label>Mês</label>
                    <select
                        value={selectedMonth}
                        onChange={e => setSelectedMonth(e.target.value)}
                        className="period-select"
                        disabled={selectedYear === 'all'}
                    >
                        <option value="all">Todos</option>
                        {availableMonths.map(m => (
                            <option key={m} value={m}>{MONTH_NAMES[parseInt(m, 10) - 1]}</option>
                        ))}
                    </select>
                </div>

                <div className="chart-type-toggle">
                    <button
                        className={`toggle-btn ${chartView === 'bar' ? 'active' : ''}`}
                        onClick={() => setChartView('bar')}
                        title="Barras"
                    ><FaChartBar /></button>
                    <button
                        className={`toggle-btn ${chartView === 'line' ? 'active' : ''}`}
                        onClick={() => setChartView('line')}
                        title="Linha"
                    ><FaChartLine /></button>
                </div>

                {/* Mini KPIs */}
                <div className="period-kpis">
                    <div className="period-kpi">
                        <span className="period-kpi-label">Receita período</span>
                        <span className="period-kpi-value green">€{kpi.revenue.toFixed(2)}</span>
                    </div>
                    <div className="period-kpi">
                        <span className="period-kpi-label">Média mensal</span>
                        <span className="period-kpi-value">€{kpi.avg.toFixed(2)}</span>
                    </div>
                    <div className="period-kpi">
                        <span className="period-kpi-label">Melhor mês</span>
                        <span className="period-kpi-value blue">{bestLabel}</span>
                    </div>
                    <div className="period-kpi">
                        <span className="period-kpi-label">Transações</span>
                        <span className="period-kpi-value">{kpi.count}</span>
                    </div>
                </div>
            </div>

            {/* ── Global overview cards ── */}
            <div className="analytics-overview-grid">
                <div className="analytics-stat-card financial-card">
                    <div className="analytics-stat-icon revenue"><FaMoneyBillWave /></div>
                    <div className="analytics-stat-info">
                        <h3>Receita Período</h3>
                        <p className="analytics-stat-number">€{(data.totalRevenue).toFixed(2)}</p>
                    </div>
                </div>
                <div className="analytics-stat-card financial-card costs-card">
                    <div className="analytics-stat-icon failed"><FaArrowDown /></div>
                    <div className="analytics-stat-info">
                        <h3>Custos Estimados</h3>
                        <p className="analytics-stat-number" style={{ color: '#ff7e5f' }}>€{data.totalCosts?.toFixed(2) ?? '0.00'}</p>
                    </div>
                    <div className="stats-card-accent" style={{ background: '#ff7e5f' }}></div>
                </div>
                <div className="analytics-stat-card financial-card balance-card">
                    <div className="analytics-stat-icon success"><FaBalanceScale /></div>
                    <div className="analytics-stat-info">
                        <h3>Saldo Real</h3>
                        <p className="analytics-stat-number" style={{ color: (data.saldo ?? 0) >= 0 ? '#43e97b' : '#ff7e5f' }}>
                            €{data.saldo?.toFixed(2) ?? '0.00'}
                        </p>
                    </div>
                </div>
                <div className="analytics-stat-card financial-card">
                    <div className="analytics-stat-icon transactions"><FaExchangeAlt /></div>
                    <div className="analytics-stat-info">
                        <h3>Total de Transações</h3>
                        <p className="analytics-stat-number">{data.totalTransactions}</p>
                    </div>
                </div>
            </div>

            {/* ── Pie / Doughnut row ── */}
            <div className="charts-grid">
                <div className="chart-item">
                    <h3>Estado dos Pagamentos (Qtd)</h3>
                    <div className="chart-wrapper">
                        <Pie
                            data={statusPieData}
                            options={pieOpts(ctx => ` ${ctx.label}: ${ctx.raw} pag.`)}
                        />
                    </div>
                </div>
                <div className="chart-item">
                    <h3>Receita por Método de Pagamento</h3>
                    <div className="chart-wrapper">
                        <Doughnut
                            data={methodDonutData}
                            options={pieOpts(ctx => ` ${ctx.label}: €${ctx.raw.toFixed(2)}`)}
                        />
                    </div>
                </div>
            </div>


            {/* ── Chart 1: Monthly revenue bar/line ── */}
            <div className="chart-item full-width">
                <h3>
                    <FaChartLine className="chart-h-icon green" />
                    Evolução de Receita Mensal
                </h3>
                <div className="chart-wrapper bar">
                    {filteredStats.length > 0 ? (
                        chartView === 'bar'
                            ? <Bar data={monthlyRevenueData} options={barOpts(false, v => '€' + v.toLocaleString('pt-PT'), ctx => ` Receita: €${ctx.raw.toFixed(2)}`)} />
                            : <Line data={monthlyRevenueData} options={lineOpts(ctx => ` Receita: €${ctx.raw.toFixed(2)}`)} />
                    ) : <div className="no-data-msg">Sem dados para o período selecionado.</div>}
                </div>
            </div>

            {/* ── Chart 2: Stacked amounts by status ── */}
            <div className="chart-item full-width">
                <h3>
                    <FaChartBar className="chart-h-icon blue" />
                    Valores por Estado (Mensal)
                </h3>
                <div className="chart-wrapper bar">
                    {filteredStats.length > 0
                        ? <Bar data={stackedStatusData} options={barOpts(true, v => '€' + v.toLocaleString('pt-PT'), ctx => ` ${ctx.dataset.label}: €${ctx.raw.toFixed(2)}`)} />
                        : <div className="no-data-msg">Sem dados para o período selecionado.</div>
                    }
                </div>
            </div>

            {/* ── Charts 3 & 4 side by side ── */}
            <div className="charts-grid">
                {/* Count per month */}
                <div className="chart-item">
                    <h3>
                        <FaExchangeAlt className="chart-h-icon orange" />
                        Nº de Pagamentos por Mês
                    </h3>
                    <div className="chart-wrapper bar">
                        {filteredStats.length > 0
                            ? <Bar data={countBarData} options={barOpts(true, v => v, ctx => ` ${ctx.dataset.label}: ${ctx.raw}`)} />
                            : <div className="no-data-msg">Sem dados.</div>
                        }
                    </div>
                </div>

                {/* Cumulative revenue */}
                <div className="chart-item">
                    <h3>
                        <FaChartLine className="chart-h-icon purple" />
                        Receita Acumulada
                    </h3>
                    <div className="chart-wrapper bar">
                        {filteredStats.length > 0
                            ? <Line data={cumulativeData} options={lineOpts(ctx => ` Acumulado: €${ctx.raw.toFixed(2)}`)} />
                            : <div className="no-data-msg">Sem dados.</div>
                        }
                    </div>
                </div>
            </div>

            {(sportRows.length > 0 || teamRows.length > 0) && (
                <>
                    <div className="charts-grid charts-grid--breakdown">
                        {/* Receita por Modalidade */}
                        <div className="chart-item">
                            <h3>
                                <FaFutbol className="chart-h-icon blue" />
                                Receita por Modalidade
                            </h3>
                            <div className="hbar-scroll-container">
                                <div style={{ height: sportChartHeight }}>
                                    {sportRows.length > 0
                                        ? <Bar data={sportBarData} options={hBarOpts(sportRows)} />
                                        : <div className="no-data-msg">Sem dados de modalidades.</div>
                                    }
                                </div>
                            </div>
                        </div>

                        {/* Custos por Modalidade — Stacked */}
                        <div className="chart-item">
                            <h3>
                                <FaCogs className="chart-h-icon coral" />
                                Custos por Modalidade
                            </h3>
                            <div className="hbar-scroll-container">
                                <div style={{ height: Math.max(BAR_MIN, (data.costsBySport?.length ?? 0) * BAR_HEIGHT) }}>
                                    {data.costsBySport?.length > 0 ? (() => {
                                        const rows = data.costsBySport;
                                        const stackedCostOpts = {
                                            indexAxis: 'y',
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: {
                                                legend: { display: true, position: 'bottom', labels: { padding: 14, usePointStyle: true, font: { size: 11, family: "'Inter', sans-serif" } } },
                                                tooltip: {
                                                    ...TOOLTIP_BASE,
                                                    callbacks: {
                                                        label: ctx => ` ${ctx.dataset.label}: €${ctx.raw.toFixed(2)}`,
                                                        afterLabel: ctx => {
                                                            const row = rows[ctx.dataIndex];
                                                            if (!row) return '';
                                                            const lines = [];
                                                            if (ctx.datasetIndex === 0) {
                                                                if (row.l1Cost > 0) lines.push(` Nív.1: €${row.l1Cost.toFixed(2)}`);
                                                                if (row.l2Cost > 0) lines.push(` Nív.2: €${row.l2Cost.toFixed(2)}`);
                                                                if (row.l3Cost > 0) lines.push(` Nív.3: €${row.l3Cost.toFixed(2)}`);
                                                            }
                                                            if (ctx.datasetIndex === 4 && row.escalaoCosts?.length > 0) {
                                                                const withArb = row.escalaoCosts.filter(e => e.arbitragem > 0);
                                                                if (withArb.length > 0) {
                                                                    lines.push(' Por Escalão:');
                                                                    withArb.forEach(e => lines.push(`   • ${e.escalaoName}: €${e.arbitragem.toFixed(2)}`));
                                                                }
                                                            }
                                                            if (ctx.datasetIndex === 5 && row.escalaoCosts?.length > 0) {
                                                                const withInsc = row.escalaoCosts.filter(e => e.inscricoes > 0);
                                                                if (withInsc.length > 0) {
                                                                    lines.push(' Por Escalão:');
                                                                    withInsc.forEach(e => lines.push(`   • ${e.escalaoName}: €${e.inscricoes.toFixed(2)}`));
                                                                }
                                                            }
                                                            if (ctx.datasetIndex === 6 && row.escalaoCosts?.length > 0) {
                                                                const withEquip = row.escalaoCosts.filter(e => e.equipamentos > 0);
                                                                if (withEquip.length > 0) {
                                                                    lines.push(' Por Escalão:');
                                                                    withEquip.forEach(e => lines.push(`   • ${e.escalaoName}: €${e.equipamentos.toFixed(2)}`));
                                                                }
                                                            }
                                                            if (ctx.datasetIndex === 7 && row.customCosts?.length > 0) {
                                                                lines.push(' Detalhe Outros:');
                                                                row.customCosts.forEach(c => lines.push(`   • ${c.label}: €${c.value.toFixed(2)}`));
                                                            }
                                                            return lines;
                                                        },
                                                        footer: ctx => {
                                                            const row = rows[ctx[0]?.dataIndex];
                                                            if (!row) return '';
                                                            return `Total Modalidade: €${(row.totalCost ?? 0).toFixed(2)}`;
                                                        }
                                                    }
                                                },
                                            },
                                            scales: {
                                                x: { ...AXIS_BASE, stacked: true, ticks: { ...AXIS_BASE.ticks, callback: v => '€' + v.toLocaleString('pt-PT') }, grid: { color: 'rgba(0,0,0,0.04)' } },
                                                y: { ...AXIS_BASE, stacked: true, grid: { display: false }, ticks: { ...AXIS_BASE.ticks, font: { size: 12, family: "'Inter', sans-serif", weight: '500' } } },
                                            },
                                            animation: { duration: 1200, easing: 'easeOutQuart' },
                                        };
                                        const coachCosts = rows.map(s => (s.l1Cost ?? 0) + (s.l2Cost ?? 0) + (s.l3Cost ?? 0));
                                        return (
                                            <Bar
                                                data={{
                                                    labels: rows.map(s => s.sport),
                                                    datasets: [
                                                        { label: 'Treinadores', data: coachCosts, backgroundColor: COST_COLORS.treinadores, borderRadius: 4, stack: 'c' },
                                                        { label: 'Atletas', data: rows.map(s => s.athleteCost ?? 0), backgroundColor: COST_COLORS.atletas, borderRadius: 4, stack: 'c' },
                                                        { label: 'Coordenação', data: rows.map(s => s.coordinationCost ?? 0), backgroundColor: COST_COLORS.coordenacao, borderRadius: 4, stack: 'c' },
                                                        { label: 'Operacionais', data: rows.map(s => s.operacionalCost ?? 0), backgroundColor: COST_COLORS.operacional, borderRadius: 4, stack: 'c' },
                                                        { label: 'Arbitragem', data: rows.map(s => s.arbitragem ?? 0), backgroundColor: COST_COLORS.arbitragem, borderRadius: 4, stack: 'c' },
                                                        { label: 'Inscrições', data: rows.map(s => s.inscricoes ?? 0), backgroundColor: COST_COLORS.inscricoes, borderRadius: 4, stack: 'c' },
                                                        { label: 'Equipamentos', data: rows.map(s => s.equipamentos ?? 0), backgroundColor: COST_COLORS.equipamentos, borderRadius: 4, stack: 'c' },
                                                        { label: 'Outros', data: rows.map(s => s.customCostTotal ?? 0), backgroundColor: COST_COLORS.custom, borderRadius: 4, stack: 'c' },
                                                    ]
                                                }}
                                                options={stackedCostOpts}
                                            />
                                        );
                                    })() : <div className="no-data-msg">Sem dados de custos.</div>}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* CUSTOS POR ESCALÃO (DENTRO DE CADA MODALIDADE) */}
                    <div className="chart-item full-width">
                        <div className="chart-header-row">
                            <h3>
                                <FaLayerGroup className="chart-h-icon blue" />
                                Custos por Escalão (dentro de cada modalidade)
                                <span className="chart-badge">{escalaoRows.length} escal{escalaoRows.length !== 1 ? 'ões' : 'ão'}</span>
                            </h3>

                            {uniqueSports.length > 2 && (
                                <div className="chart-inline-filter">
                                    <select
                                        value={escalaoSportFilter}
                                        onChange={(e) => setEscalaoSportFilter(e.target.value)}
                                        className="chart-select-filter"
                                    >
                                        {uniqueSports.map(s => (
                                            <option key={s} value={s}>{s === 'Todas' ? 'Todas as Modalidades' : s}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>

                        <div className="hbar-scroll-container">
                            <div style={{ height: Math.max(BAR_MIN, escalaoRows.length * BAR_HEIGHT) }}>
                                {escalaoRows.length > 0 ? (() => {
                                    const stackedEscalaoOpts = {
                                        indexAxis: 'y',
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        plugins: {
                                            legend: {
                                                display: true,
                                                position: 'bottom',
                                                labels: { padding: 14, usePointStyle: true, font: { size: 11, family: "'Inter', sans-serif" } }
                                            },
                                            tooltip: {
                                                ...TOOLTIP_BASE,
                                                callbacks: {
                                                    label: ctx => ` ${ctx.dataset.label}: €${ctx.raw.toFixed(2)}`,
                                                    afterLabel: ctx => {
                                                        const row = escalaoRows[ctx.dataIndex];
                                                        if (!row) return '';
                                                        const lines = [];
                                                        if (ctx.datasetIndex === 0) {
                                                            if (row.l1Cost > 0) lines.push(`   • Nível 1: €${row.l1Cost.toFixed(2)}`);
                                                            if (row.l2Cost > 0) lines.push(`   • Nível 2: €${row.l2Cost.toFixed(2)}`);
                                                            if (row.l3Cost > 0) lines.push(`   • Nível 3: €${row.l3Cost.toFixed(2)}`);
                                                        }
                                                        if (ctx.datasetIndex === 1 && row.athleteCount > 0) {
                                                            lines.push(`   • ${row.athleteCount} atletas ativos`);
                                                        }
                                                        if (ctx.datasetIndex === 5 && row.customCosts?.length > 0) {
                                                            row.customCosts.forEach(c => lines.push(`   • ${c.label}: €${c.value.toFixed(2)}`));
                                                        }
                                                        return lines;
                                                    },
                                                    footer: ctx => {
                                                        const row = escalaoRows[ctx[0]?.dataIndex];
                                                        if (!row) return '';
                                                        return `Custo Total Escalão: €${(row.totalCost ?? 0).toFixed(2)}`;
                                                    }
                                                }
                                            }
                                        },
                                        scales: {
                                            x: {
                                                ...AXIS_BASE,
                                                stacked: true,
                                                ticks: { ...AXIS_BASE.ticks, callback: v => '€' + v.toLocaleString('pt-PT') },
                                                grid: { color: 'rgba(0,0,0,0.04)' }
                                            },
                                            y: {
                                                ...AXIS_BASE,
                                                stacked: true,
                                                grid: { display: false },
                                                ticks: { ...AXIS_BASE.ticks, font: { size: 12, family: "'Inter', sans-serif", weight: '500' } }
                                            }
                                        },
                                        animation: { duration: 1200, easing: 'easeOutQuart' }
                                    };

                                    return (
                                        <Bar
                                            data={{
                                                labels: escalaoRows.map(r => r.label),
                                                datasets: [
                                                    { label: 'Treinadores', data: escalaoRows.map(r => r.coachCost ?? 0), backgroundColor: COST_COLORS.treinadores, borderRadius: 4, stack: 'esc' },
                                                    { label: 'Atletas', data: escalaoRows.map(r => r.athleteCost ?? 0), backgroundColor: COST_COLORS.atletas, borderRadius: 4, stack: 'esc' },
                                                    { label: 'Arbitragem', data: escalaoRows.map(r => r.arbitragem ?? 0), backgroundColor: COST_COLORS.arbitragem, borderRadius: 4, stack: 'esc' },
                                                    { label: 'Inscrições', data: escalaoRows.map(r => r.inscricoes ?? 0), backgroundColor: COST_COLORS.inscricoes, borderRadius: 4, stack: 'esc' },
                                                    { label: 'Equipamentos', data: escalaoRows.map(r => r.equipamentos ?? 0), backgroundColor: COST_COLORS.equipamentos, borderRadius: 4, stack: 'esc' },
                                                    { label: 'Outros', data: escalaoRows.map(r => r.customCost ?? 0), backgroundColor: COST_COLORS.custom, borderRadius: 4, stack: 'esc' },
                                                ]
                                            }}
                                            options={stackedEscalaoOpts}
                                        />
                                    );
                                })() : <div className="no-data-msg">Sem dados de custos por escalão para o filtro selecionado.</div>}
                            </div>
                        </div>
                    </div>

                    {/* RENTABILIDADE POR EQUIPA (RECOMBINED CHART) */}
                    <div className="chart-item full-width">
                        <div className="chart-header-row">
                            <h3>
                                <FaBalanceScale className="chart-h-icon teal" />
                                Análise de Rentabilidade por Equipa (Receita vs Custos)
                                <span className="chart-badge">{teamRows.length} equip{teamRows.length !== 1 ? 'as' : 'a'}</span>
                            </h3>

                            {uniqueSports.length > 2 && (
                                <div className="chart-inline-filter">
                                    <select
                                        value={teamSportFilter}
                                        onChange={(e) => setTeamSportFilter(e.target.value)}
                                        className="chart-select-filter"
                                    >
                                        {uniqueSports.map(s => (
                                            <option key={s} value={s}>{s === 'Todas' ? 'Todas as Modalidades' : s}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>

                        <div className="hbar-scroll-container">
                            <div style={{ height: Math.max(400, teamRows.length * 60) }}>
                                {teamRows.length > 0 ? (() => {
                                    const baseOpts = hBarOpts(teamRows);
                                    return (
                                        <Bar
                                            data={{
                                                labels: teamRows.map(r => r.team),
                                                datasets: [
                                                    {
                                                        label: 'Receita (€)',
                                                        data: teamRows.map(r => r.totalAmount),
                                                        backgroundColor: 'rgba(67,233,123,0.85)',
                                                        borderRadius: 4,
                                                    },
                                                    {
                                                        label: 'Custos (€)',
                                                        data: teamRows.map(r => {
                                                            const costRow = data.costsByTeam?.find(c => c.teamName === r.team);
                                                            return costRow ? costRow.totalCost : 0;
                                                        }),
                                                        backgroundColor: 'rgba(255,126,95,0.85)',
                                                        borderRadius: 4,
                                                    }
                                                ]
                                            }}
                                            options={{
                                                ...baseOpts,
                                                plugins: {
                                                    ...baseOpts.plugins,
                                                    legend: { display: true, position: 'top' },
                                                    tooltip: {
                                                        ...baseOpts.plugins.tooltip,
                                                        callbacks: {
                                                            label: ctx => ` ${ctx.dataset.label}: €${ctx.raw.toFixed(2)}`,
                                                            afterLabel: ctx => {
                                                                if (ctx.datasetIndex === 0) { // Revenue series
                                                                    const row = teamRows[ctx.dataIndex];
                                                                    return row?.count !== undefined ? ` ${row.count} pagamento${row.count !== 1 ? 's' : ''}` : '';
                                                                } else if (ctx.datasetIndex === 1) { // Costs series
                                                                    const row = data.costsByTeam?.find(c => c.teamName === teamRows[ctx.dataIndex].team);
                                                                    if (!row) return '';
                                                                    const lines = [];

                                                                    if (row.escalaoName && row.escalaoName !== 'Sem Escalão') {
                                                                        lines.push(` Escalão: ${row.escalaoName}`);
                                                                    }
                                                                    if (row.l1Cost > 0) {
                                                                        lines.push(` Nível 1: €${row.l1Cost.toFixed(2)}`);
                                                                        if (row.l1Names) wrapText(row.l1Names, 40).forEach(l => lines.push(`   ${l}`));
                                                                    }
                                                                    if (row.l2Cost > 0) {
                                                                        lines.push(` Nível 2: €${row.l2Cost.toFixed(2)}`);
                                                                        if (row.l2Names) wrapText(row.l2Names, 40).forEach(l => lines.push(`   ${l}`));
                                                                    }
                                                                    if (row.l3Cost > 0) {
                                                                        lines.push(` Nível 3: €${row.l3Cost.toFixed(2)}`);
                                                                        if (row.l3Names) wrapText(row.l3Names, 40).forEach(l => lines.push(`   ${l}`));
                                                                    }
                                                                    if (row.athleteCost > 0) lines.push(` Atletas: €${row.athleteCost.toFixed(2)}`);
                                                                    if (row.arbitragem > 0) lines.push(` Arbitragem: €${row.arbitragem.toFixed(2)}`);
                                                                    if (row.inscricoes > 0) lines.push(` Inscrições: €${row.inscricoes.toFixed(2)}`);
                                                                    if (row.equipamentos > 0) lines.push(` Equipamentos: €${row.equipamentos.toFixed(2)}`);
                                                                    if (row.customCost > 0) lines.push(` Outros: €${row.customCost.toFixed(2)}`);
                                                                    lines.push(` Custo Total Equipa: €${(row.totalCost ?? 0).toFixed(2)}`);
                                                                    return lines;
                                                                }
                                                                return '';
                                                            }
                                                        }
                                                    }
                                                }
                                            }}
                                        />
                                    );
                                })() : <div className="no-data-msg">Sem dados.</div>}
                            </div>
                        </div>
                    </div>

                    <div className="charts-grid charts-grid--breakdown">
                        {/* Receita dos Sócios */}
                        <div className="chart-item">
                            <h3>
                                <FaUsers className="chart-h-icon blue" />
                                Receita dos Sócios (Sem Equipa/Modalidade)
                            </h3>
                            <div className="hbar-scroll-container" style={{ maxHeight: '200px' }}>
                                <div style={{ height: 100 }}>
                                    {unassignedSport ? (
                                        <Bar data={membersRevenueData} options={hBarOpts([unassignedSport])} />
                                    ) : (
                                        <div className="no-data-msg">Nenhuma receita de sócios.</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

        </div>
    );
};

export default FinancialAnalytics;